import { createFileRoute } from '@tanstack/react-router'
import { CtaButton } from '@workspace/ui/components/dsm/cta-button'
import { Plus } from 'lucide-react'
import { useState } from 'react'
import {
  useAdminCreateHelpCategoryMutation,
  useAdminHelpCategoriesQuery,
  useAdminReorderHelpCategoriesMutation,
} from '../../../features/admin/hooks/use-admin-api'
import { CategoryForm } from '../../../features/help-center/components/category-form'
import { CategorySection } from '../../../features/help-center/components/category-section'

export const Route = createFileRoute('/admin/help-center/')({
  component: Page,
})

function Page() {
  const [isCreating, setIsCreating] = useState(false)
  const [saveMessage, setSaveMessage] = useState<{
    type: 'success' | 'error'
    text: string
  } | null>(null)

  const categoriesQuery = useAdminHelpCategoriesQuery()
  const createMutation = useAdminCreateHelpCategoryMutation()
  const reorderMutation = useAdminReorderHelpCategoriesMutation()

  const categories = categoriesQuery.data?.data?.items ?? []

  const showTemporaryMessage = (type: 'success' | 'error', text: string) => {
    setSaveMessage({ type, text })
    setTimeout(() => setSaveMessage(null), 3000)
  }

  const moveCategory = (index: number, direction: -1 | 1) => {
    const orderedIds = categories.map((c) => c.id)
    const target = index + direction
    if (target < 0 || target >= orderedIds.length) return
    const current = orderedIds[index]
    const swapped = orderedIds[target]
    if (current === undefined || swapped === undefined) return
    orderedIds[index] = swapped
    orderedIds[target] = current
    reorderMutation.mutate(
      { body: { orderedIds } },
      { onError: () => showTemporaryMessage('error', 'Failed to reorder categories') }
    )
  }

  if (categoriesQuery.isLoading) {
    return (
      <div className="flex h-48 items-center justify-center text-muted-foreground">Loading...</div>
    )
  }

  return (
    <div className="w-full space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold">Help Center</h1>
        {!isCreating && (
          <CtaButton size="sm" icon={<Plus />} onClick={() => setIsCreating(true)}>
            New category
          </CtaButton>
        )}
      </div>

      {isCreating && (
        <div className="rounded-lg border border-border p-4">
          <CategoryForm
            onSubmit={(data) =>
              createMutation.mutate(
                { body: { name: data.name, description: data.description || undefined } },
                {
                  onSuccess: () => setIsCreating(false),
                  onError: () => showTemporaryMessage('error', 'Failed to create category'),
                }
              )
            }
            onCancel={() => setIsCreating(false)}
            isSubmitting={createMutation.isPending}
          />
        </div>
      )}

      {categories.length === 0 && !isCreating ? (
        <p className="py-12 text-center text-sm text-muted-foreground">
          No categories yet. Create one to start organizing help articles.
        </p>
      ) : (
        categories.map((category, index) => (
          <CategorySection
            key={category.id}
            category={category}
            isFirst={index === 0}
            isLast={index === categories.length - 1}
            isReordering={reorderMutation.isPending}
            onMoveUp={() => moveCategory(index, -1)}
            onMoveDown={() => moveCategory(index, 1)}
            onMutationError={(message) => showTemporaryMessage('error', message)}
          />
        ))
      )}

      {saveMessage && (
        <div
          className={`fixed bottom-6 right-6 rounded-lg px-4 py-2.5 text-sm font-medium shadow-lg ${
            saveMessage.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
          }`}
        >
          {saveMessage.text}
        </div>
      )}
    </div>
  )
}
